import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { employeeAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Loader2, Send, Building2, Mail, Briefcase, Clock, CheckCircle, 
  XCircle, AlertCircle, RefreshCw, Trash2, Plus, Calendar, FileText
} from 'lucide-react';

const RecordRequests = () => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    employer_name: '',
    employer_email: '',
    job_title: '',
    department: '',
    start_date: '',
    end_date: '',
    employment_type: '',
    custom_message: ''
  });

  const employmentTypes = [
    'Full-time',
    'Part-time',
    'Contract',
    'Freelance',
    'Internship',
    'Temporary'
  ];

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await employeeAPI.getRecordRequests();
      setRequests(res.data);
    } catch (error) {
      toast.error('Failed to load record requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.employer_name || !formData.employer_email || !formData.job_title || !formData.start_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    // RFC 5322 compliant - simple @ check, no domain validation
    if (!formData.employer_email.includes('@') || !formData.employer_email.includes('.')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    try {
      await employeeAPI.createRecordRequest(formData);
      toast.success(`Record request sent to ${formData.employer_email}`);
      setShowModal(false);
      setFormData({
        employer_name: '',
        employer_email: '',
        job_title: '',
        department: '',
        start_date: '',
        end_date: '',
        employment_type: '',
        custom_message: ''
      });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send record request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (id) => {
    try {
      await employeeAPI.resendRecordRequest(id);
      toast.success('Reminder sent');
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const handleCancel = async (id) => {
    try {
      await employeeAPI.cancelRecordRequest(id);
      toast.success('Request cancelled');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, color: 'text-amber-400 bg-amber-500/20 border-amber-500/30', label: 'Pending' },
      accepted: { icon: CheckCircle, color: 'text-green-400 bg-green-500/20 border-green-500/30', label: 'Accepted' },
      declined: { icon: XCircle, color: 'text-red-400 bg-red-500/20 border-red-500/30', label: 'Declined' },
      expired: { icon: AlertCircle, color: 'text-[#5B6472] bg-[#F5F8FF] border-[#E8EDF5]', label: 'Expired' },
      cancelled: { icon: XCircle, color: 'text-[#5B6472] bg-[#F5F8FF] border-[#E8EDF5]', label: 'Cancelled' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="record-requests-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">Request Records</h1>
            <p className="mt-1 text-[#5B6472] text-sm">
              Request VETT records from your past employers to build your verified work history.
            </p>
          </div>
          <Button
            onClick={() => setShowModal(true)}
            disabled={pendingCount >= 5}
            className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
            data-testid="request-record-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Request Record
          </Button>
        </div>

        {/* Pending Limit Warning */}
        {pendingCount >= 5 && (
          <Card className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              <p className="text-amber-400 text-sm">
                You have reached the maximum of 5 pending requests. Please wait for responses or cancel existing requests.
              </p>
            </div>
          </Card>
        )}

        {/* Request List */}
        {requests.length === 0 ? (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-12 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#F5F8FF] flex items-center justify-center mb-4">
              <FileText className="w-8 h-8 text-[#5B6472]" />
            </div>
            <h3 className="text-[#0A0F1E] font-semibold text-lg mb-2">No Record Requests Yet</h3>
            <p className="text-[#5B6472] mb-6 max-w-md mx-auto">
              Request VETT records from past employers to verify your work history and boost your credibility.
            </p>
            <Button
              onClick={() => setShowModal(true)}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
            >
              <Send className="w-4 h-4 mr-2" />
              Request Your First Record
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {requests.map((req) => (
              <Card 
                key={req.id}
                className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 hover:border-[#1B5AFF]/30 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#F5F8FF] flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-[#5B6472]" />
                    </div>
                    <div>
                      <h3 className="text-[#0A0F1E] font-semibold">{req.employer_name}</h3>
                      <p className="text-[#1B5AFF] text-sm">{req.job_title}</p>
                      <div className="flex items-center gap-4 mt-2 text-[#5B6472] text-sm">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" />
                          {req.employer_email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {req.start_date} - {req.end_date || 'Present'}
                        </span>
                      </div>
                      <p className="text-[#5B6472] text-xs mt-2">
                        Requested {formatDate(req.created_at)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-2">
                    {getStatusBadge(req.status)}
                    
                    {req.status === 'pending' && (
                      <div className="flex gap-2 mt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResend(req.id)}
                          className="h-8 px-3 text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#F5F8FF]"
                        >
                          <RefreshCw className="w-3.5 h-3.5 mr-1" />
                          Remind
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancel(req.id)}
                          className="h-8 px-3 text-[#5B6472] hover:text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                    
                    {req.status === 'accepted' && (
                      <p className="text-green-400 text-xs mt-2">
                        Record being created
                      </p>
                    )}
                    
                    {req.status === 'declined' && req.decline_reason && (
                      <p className="text-[#5B6472] text-xs mt-2 max-w-[200px] text-right">
                        {req.decline_reason}
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Request Record Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#1B5AFF]" />
              Request VETT Record
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Company Name *</Label>
              <Input
                value={formData.employer_name}
                onChange={(e) => setFormData(prev => ({ ...prev, employer_name: e.target.value }))}
                placeholder="e.g., Acme Corporation"
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-11"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Contact Email *</Label>
              <Input
                type="email"
                value={formData.employer_email}
                onChange={(e) => setFormData(prev => ({ ...prev, employer_email: e.target.value }))}
                placeholder="hr@company.com or manager@gmail.com"
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-11"
              />
              <p className="text-[#5B6472] text-xs">HR email, manager email, or any valid contact email</p>
            </div>

            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Job Title *</Label>
              <Input
                value={formData.job_title}
                onChange={(e) => setFormData(prev => ({ ...prev, job_title: e.target.value }))}
                placeholder="e.g., Software Engineer"
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-11"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Department (Optional)</Label>
              <Input
                value={formData.department}
                onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                placeholder="e.g., Engineering"
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-11"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-11"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-11"
                />
                <p className="text-[#5B6472] text-xs">Leave blank if current</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Employment Type</Label>
              <Select 
                value={formData.employment_type} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, employment_type: v }))}
              >
                <SelectTrigger className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-11">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                  {employmentTypes.map(type => (
                    <SelectItem key={type} value={type} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Personal Message (Optional)</Label>
              <Textarea
                value={formData.custom_message}
                onChange={(e) => setFormData(prev => ({ ...prev, custom_message: e.target.value }))}
                placeholder="Add context or a personal note to the employer..."
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[80px]"
              />
            </div>

            <DialogFooter className="gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Request
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default RecordRequests;
